import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
    Alert,
    Button,
    CardHeader,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField
} from "@mui/material";
import { withStyles } from "@mui/styles";
import axios from "axios";
import * as React from "react";
import { InventoryIcon } from "../../components/InventoryIcon";
import { Config } from "../../config/config";
import { getGlobalVar, Helpers } from "../../utils/helpers";
import { PopupMessage } from "../../utils/message";

interface PlayerWeaponsState {
    loaded: boolean;
    weapons: any;
    activeLevelForm: null | string;
    levelFormVisible: boolean;
    filterKeyword: string;
    originalWeaponTokens: number;
    modalVisible: boolean;
    checkedList: any[];
    indeterminate: boolean;
    checkAll: boolean;
    filter: string;
    level: number;
    inlineFormLevel: number;
    savingData: boolean;
}

interface PlayerWeaponsProps {
    playerId: string;
    activeEnv: any;
    playerData: any;
    heroesConfig: any;
    classes: any;
}

class PlayerWeaponsMain extends React.Component<PlayerWeaponsProps, PlayerWeaponsState> {

    constructor(props) {
        super(props);

        this.state = {
            loaded: false,
            weapons: [],
            levelFormVisible: false,
            activeLevelForm: null,
            filterKeyword: "",
            originalWeaponTokens: 0,
            modalVisible: false,
            checkedList: [],
            indeterminate: true,
            checkAll: false,
            filter: "weapons",
            level: 1,
            inlineFormLevel: 1,
            savingData: false,
        };
    }

    componentDidMount() {
        this.fetchData();
    }

    addAttachments = () => {
        this.setState({ savingData: true }, () => {
            axios.post(`${Config.getInstance.apiUrl}/api/user/weapons`, {
                attachments: this.state.checkedList,
                userId: this.props.playerId,
                level: this.state.level,
            }, {
                headers: {
                    Authorization: Helpers.getSessionId(),
                    env: this.props.activeEnv,
                },
            })
            .then(data => {
                this.setState({
                    checkedList: [],
                    indeterminate: true,
                    checkAll: false,
                    modalVisible: false,
                    filter: "all",
                    level: 1,
                    loaded: false,
                    savingData: false,
                }, () => {
                    this.fetchData();
                });
            })
            .catch(() => {
                this.setState({ savingData: false });
                PopupMessage.error("Something went wrong!");
            });
        });
    }

    fetchData = () =>
        axios.get(`${Config.getInstance.apiUrl}/api/user/${this.props.playerId}/attachments`, {
            headers: { Authorization: Helpers.getSessionId(), env: this.props.activeEnv },
        })
        .then(weapons => {
            this.setState({
                loaded: true,
                weapons: weapons.data.data || [],
            });
        })
        .catch((_err) => {
            PopupMessage.error("Something went wrong");
        })

    refreshList = () => {
        this.setState({ loaded: false }, () => {
            this.fetchData();
        });
    }

    findCapoNameById = (capoId) => {
        const weaponConfig = this.props.heroesConfig.find(config2 => parseInt(config2.id as any as string, 10) === capoId);
        return weaponConfig ? Helpers.getLocalizationName(weaponConfig.name) : capoId;
    }

    openForm = (levelId, level) => this.setState({
        levelFormVisible: true,
        activeLevelForm: levelId,
        inlineFormLevel: level,
    })

    closeForm = () => this.setState({
        levelFormVisible: false,
        activeLevelForm: null,
    })

    handleLevelChange = () => {
        if (!this.state.activeLevelForm) {
            return;
        }

        const id = this.state.activeLevelForm;

        axios.put(`${Config.getInstance.apiUrl}/api/user/weapon/level`, {
            level: this.state.inlineFormLevel,
            userId: this.props.playerId,
            id,
        }, {
            headers: {
                Authorization: Helpers.getSessionId(),
                env: this.props.activeEnv,
            },
        })
        .then(data => {
            const tempWeapons = [ ...this.state.weapons ];
            const followerItem = tempWeapons.find(weapon => weapon.id === id);
            followerItem.level = this.state.inlineFormLevel;

            return this.setState({
                weapons: tempWeapons,
                levelFormVisible: false,
                activeLevelForm: null,
            });
        })
        .catch(() => {
            PopupMessage.error("Something went wrong!");
        });
    }

    deleteWeapon = (id) => {
        axios.delete(`${Config.getInstance.apiUrl}/api/user/${this.props.playerId}/weapon/${id}`, {
            headers: {
                Authorization: Helpers.getSessionId(),
                env: this.props.activeEnv,
            },
        })
        .then(data => {
            const tempWeapons = [ ...this.state.weapons ];
            const followerItemIndex = tempWeapons.findIndex(weapon => weapon.id === id);
            tempWeapons.splice(followerItemIndex, 1);

            return this.setState({
                weapons: tempWeapons,
                levelFormVisible: false,
                activeLevelForm: null,
            });
        })
        .catch(() => {
            PopupMessage.error("Something went wrong!");
        });
    }

    _handleKeyPress = (e) => {
        if (e.key === "Enter") {
            return this.handleLevelChange();
        }
    }

    onInlineLevelChange = (e) => {
        this.setState({ inlineFormLevel: +e.target.value });
    }

    handleModalCancel = () => this.setState({
        checkedList: [],
        indeterminate: true,
        checkAll: false,
        modalVisible: false,
        filter: "weapons",
        level: 1,
    })

    showModal = () => this.setState({ modalVisible: true });

    getAttachments = () => getGlobalVar("ITEMS").filter(item => item.type === "weapon" || item.type === "torso" || item.type === "legs");

    getCheckBoxAttachmentsValues = () => {
        if (this.state.filter === "weapons") {
            return this.getAttachments().filter(item => item.type === "weapon").map(item => item.id);
        }

        if (this.state.filter === "torso") {
            return this.getAttachments().filter(item => item.type === "torso").map(item => item.id);
        }

        if (this.state.filter === "legs") {
            return this.getAttachments().filter(item => item.type === "legs").map(item => item.id);
        }

        if (this.state.filter === "all") {
            return this.getAttachments().map(item => item.id);
        }

        return [];
    }

    getCheckBoxAttachments = () => {
        if (this.state.filter === "weapons") {
            return this.getAttachments().filter(item => item.type === "weapon").map(item => ({
                label: Helpers.getLocalizationName(item.name),
                value: item.id,
            }));
        }

        if (this.state.filter === "torso") {
            return this.getAttachments().filter(item => item.type === "torso").map(item => ({
                label: Helpers.getLocalizationName(item.name),
                value: item.id,
            }));
        }

        if (this.state.filter === "legs") {
            return this.getAttachments().filter(item => item.type === "legs").map(item => ({
                label: Helpers.getLocalizationName(item.name),
                value: item.id,
            }));
        }

        if (this.state.filter === "all") {
            return this.getAttachments().map(item => ({
                label: Helpers.getLocalizationName(item.name),
                value: item.id,
            }));
        }

        return [];
    }

    onCheckAllChange = e => {
        this.setState({
            checkedList: e.target.checked ? this.getCheckBoxAttachmentsValues() : [],
            indeterminate: false,
            checkAll: e.target.checked,
        });
    }

    onChange = e => {
        const tempList = [ ...this.state.checkedList ];
        const itemIndex = tempList.findIndex(item => item === +e.target.value);

        if (itemIndex > -1) {
            tempList.splice(itemIndex, 0);
        } else {
            tempList.push(+e.target.value);
        }

        this.setState({
            checkedList: tempList,
            indeterminate: !!tempList.length && tempList.length < this.getCheckBoxAttachmentsValues().length,
            checkAll: tempList.length === this.getCheckBoxAttachmentsValues().length,
        });
    }

    handleFilterChange = e => this.setState({
        checkedList: [],
        indeterminate: true,
        checkAll: false,
        filter: e.target.value,
    })

    changeInitLevel = e => this.setState({ level: e.target.value });

    getNameById = (attachmentId) => {
        const item = getGlobalVar("ITEMS").find(itemObject => itemObject.id === +attachmentId);
        return item ? Helpers.getLocalizationName(item.name) : attachmentId;
    }

    levelForm() {
        const formItemStyle = {
            display: "inline-block",
        };

        const formButtonStyle = {
            display: "inline-block",
            fontSize: 20,
            marginLeft: 5,
        };

        const successButtonStyle = {
            ...formButtonStyle,
            color: "green",
        };

        const cancelButtonStyle = {
            ...formButtonStyle,
        };

        return <div style={{ display: "inline-block" }}>
            <div style={formItemStyle}>
                <TextField
                    InputProps={{ classes: { input: this.props.classes.smallInput } }}
                    id="outlined-number"
                    value={this.state.inlineFormLevel}
                    onKeyDown={this._handleKeyPress}
                    onChange={this.onInlineLevelChange}
                    type="number"
                    variant="outlined"
                />
            </div>
            <div style={{position: "absolute", left: 119, top: 17, minWidth: 50 }}>
                <div style={successButtonStyle}>
                    <CheckCircleIcon onClick={() => this.handleLevelChange()}/>
                </div>
                <div style={cancelButtonStyle}>
                    <CancelIcon onClick={() => this.closeForm()}/>
                </div>
            </div>
        </div>;
    }

    toggleLevelInlineForm = (data) => {
        if (this.state.levelFormVisible && data.id === this.state.activeLevelForm) {
            return this.levelForm();
        }

        if (this.props.playerData.is_online) {
            return data.level;
        }

        return <span onClick={() => this.openForm(data.id, data.level)} className="edit--field-unactive">
            {data.level}
        </span>;
    };

    renderRows() {
        if (!this.state.loaded) {
            return <TableRow key="loading-row">
                <TableCell colSpan={6}>
                    {Helpers.renderSkeletonRows(3, 50)}
                </TableCell>
            </TableRow>
        }

        if (this.state.weapons.length === 0) {
            return <TableRow key="records_not_found">
                <TableCell colSpan={6}>
                    <Alert severity="info">No Heroes</Alert>
                </TableCell>
            </TableRow>
        }

        return (this.state.weapons || []).map(item => (
            <TableRow key={item.id}>
                <TableCell>{item.id}</TableCell>
                <TableCell>{item.attachmentId}</TableCell>
                <TableCell style={{ display: "flex", alignItems: "center" }}>
                    {InventoryIcon({
                        itemId: item.attachmentId,
                        style: { marginRight: 10 },
                    })}
                    {this.getNameById(item.attachmentId)}
                </TableCell>
                <TableCell>{!!item.assignedCapo && this.findCapoNameById(item.assignedCapo)}</TableCell>
                <TableCell style={{ position: "relative" }}>{this.toggleLevelInlineForm(item)}</TableCell>
                <TableCell>{!item.assignedCapo && <div style={{ marginTop: 5 }}>
                    {Helpers.conditionPrint(this.props.playerData.is_online,
                        <Button disableElevation
                            size="small"
                            onClick={() => this.deleteWeapon(item.id)}
                            variant="contained"
                            color="secondary"
                        >
                            Remove
                        </Button>
                    , "")}
                </div>}</TableCell>
            </TableRow>
        ));
    }

    renderAction() {
        if (this.props.playerData.is_online) {
            return null;
        }

        if (!this.state.loaded) {
            return <Skeleton
                width={160}
                height={60}
            />
        }

        return <Button disableElevation onClick={this.showModal} variant="contained">Add attachment</Button>;
    }

    render() {
        return <React.Fragment>
            <CardHeader
                action={this.renderAction()}
            />

            <Paper elevation={0}>
                <Table className={this.props.classes.table}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Unique Id</TableCell>
                            <TableCell>Game Id</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Assigned</TableCell>
                            <TableCell>Level</TableCell>
                            <TableCell style={{ width: 150 }}>Delete</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {this.renderRows()}
                    </TableBody>
                </Table>
            </Paper>

            <Dialog
                open={this.state.modalVisible}
                onClose={this.handleModalCancel}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                fullWidth={true}
                maxWidth="md"
            >
                <DialogTitle id="alert-dialog-title">
                    Add new attachment
                </DialogTitle>
                <DialogContent>
                    <div style={{ marginBottom: 15 }}>
                        <div style={{ display: "inline-block", marginRight: 25 }}>
                            <FormControlLabel
                                control={<Checkbox indeterminate={this.state.indeterminate} onChange={this.onCheckAllChange} checked={this.state.checkAll}/>}
                                label="Select All"
                                labelPlacement="start"
                            />
                        </div>
                        <div style={{ display: "inline-block", marginRight: 25 }}>
                            <FormControlLabel
                                control={
                                    <Select
                                        onChange={this.handleFilterChange}
                                        value={this.state.filter}
                                        style={{ marginLeft: 10 }}
                                        size="small"
                                    >
                                        <MenuItem value="all">All</MenuItem>
                                        <MenuItem value="weapons">Weapons</MenuItem>
                                        <MenuItem value="torso">Torso</MenuItem>
                                        <MenuItem value="legs">Legs</MenuItem>
                                    </Select>
                                }
                                label="Attachment type"
                                labelPlacement="start"
                            />
                        </div>
                        <div style={{ display: "inline-block", marginRight: 25 }}>
                            <FormControlLabel
                                control={
                                    <TextField
                                        InputProps={{ classes: { input: this.props.classes.smallInput } }}
                                        value={this.state.level}
                                        onChange={this.changeInitLevel}
                                        type="number"
                                        variant="outlined"
                                        style={{ marginLeft: 10 }}
                                        size="small"
                                    />
                                }
                                label="Default level"
                                labelPlacement="start"
                            />
                        </div>
                    </div>

                    <FormGroup row={true}>
                        {this.getCheckBoxAttachments().map(option => (
                            <FormControlLabel key={option.value} control={this.checkboxItem(option)} label={option.label}/>
                        ))}
                    </FormGroup>
                </DialogContent>
                <DialogActions>
                    <Button disableElevation onClick={this.handleModalCancel} color="primary" disabled={this.state.savingData}>
                        Cancel
                    </Button>

                    <div className={this.props.classes.buttonWrapper}>
                        <Button disableElevation onClick={this.addAttachments} color="primary" disabled={this.state.savingData}>
                            Add attachments
                        </Button>

                        {this.state.savingData && <CircularProgress className={this.props.classes.buttonProgress} size={24}/>}
                    </div>

                </DialogActions>
            </Dialog>
        </React.Fragment>;
    }

    checkboxItem = (option) => {
        const checked = this.state.checkedList.findIndex(item => +item === +option.value) > -1;
        return <Checkbox value={option.value} onChange={this.onChange} checked={checked}/>;
    }
}

const styles: any = theme => ({
    smallInput: {
        height: 0,
        fontSize: "15px",
        width: 62,
        padding: "16.5px 14px",
    },
    buttonWrapper: {
        margin: 8,
        position: "relative",
    },
    buttonProgress: {
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
});

export const PlayerWeapons = withStyles(styles)(PlayerWeaponsMain);
